import { isValidSpaceHostSlug, parseSpaceUrl } from "../../shared/spaceSlug";

type TrustPolicy = {
  origins: Set<string>;
  customDomains: string[];
  spaceOrigin?: string;
};

type TenantOrigins = {
  tenantOrigin: string;
  tenantEdgeOrigin: string;
  tenantEdgeWildcardDomain: string | null;
  tenantCustomDomains?: string[];
  tenantApiOrigin: string;
};

// The origins a callbackURL may target are rooted in the authenticated tenant’s
// canonical origins.
function trustPolicy(tenantOrigins: TenantOrigins): TrustPolicy {
  const tenantOrigin = new URL(tenantOrigins.tenantOrigin);
  const edgeOrigin = new URL(tenantOrigins.tenantEdgeOrigin);
  const origins = new Set([
    tenantOrigin.origin,
    edgeOrigin.origin,
    new URL(tenantOrigins.tenantApiOrigin).origin,
  ]);
  return {
    origins,
    customDomains: tenantOrigins.tenantCustomDomains ?? [],
    spaceOrigin: tenantOrigins.tenantEdgeWildcardDomain
      ? edgeOrigin.origin
      : undefined,
  };
}

/**
 * Reads the `callbackURL` query param and validates it against the set of
 * origins we trust, so a crafted link can't use the sign-in page as an
 * open redirect.
 */
export function readTrustedSignInCallbackURL(
  tenantOrigins: TenantOrigins,
  search: string = window.location.search
): string | null {
  const raw = new URLSearchParams(search).get("callbackURL");
  if (!raw) return null;

  let parsed: URL;
  try {
    parsed = new URL(raw, window.location.origin);
  } catch {
    return null;
  }
  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
    return null;
  }

  const { origins, customDomains, spaceOrigin } = trustPolicy(tenantOrigins);
  if (origins.has(parsed.origin)) return parsed.toString();
  if (
    spaceOrigin &&
    parseSpaceUrl(parsed, true)?.tenantOrigin === spaceOrigin
  ) {
    return parsed.toString();
  }
  if (
    parsed.protocol === "https:" &&
    !parsed.port &&
    customDomains.some((domain) => {
      const suffix = `.${domain.toLowerCase()}`;
      return (
        parsed.hostname.endsWith(suffix) &&
        isValidSpaceHostSlug(parsed.hostname.slice(0, -suffix.length), false)
      );
    })
  ) {
    return parsed.toString();
  }
  return null;
}
